import React, { useEffect, useState } from "react";
import { useConfetti } from "../context/ConfettiContext";

const ConfettiRain = () => {
	const { confettiActive, confettiKey } = useConfetti();
	const [confettiPieces, setConfettiPieces] = useState([]);

	useEffect(() => {
		if (!confettiActive) {
			// Don't immediately clear confetti pieces - let them fade out naturally
			return;
		}

		const colors = [
			"#ff6b6b",
			"#4ecdc4",
			"#45b7d1",
			"#96ceb4",
			"#feca57",
			"#ff9ff3",
			"#54a0ff",
		];
		const shapes = ["square", "circle", "triangle"];

		// Detect Safari for performance optimizations
		const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

		const generationDuration = 6000; // Generate confetti bursts for 6 seconds
		const generationInterval = isSafari ? 600 : 450; // Slower on Safari to reduce load
		const piecesPerBurst = isSafari ? 6 : 10; // Fewer pieces on Safari
		const maxPieces = isSafari ? 60 : 100; // Lower max on Safari

		// Generate clustered bursts of confetti pieces
		const generationTimer = setInterval(() => {
			setConfettiPieces((prevPieces) => {
				const limitedPieces =
					prevPieces.length >= maxPieces
						? prevPieces.slice(-maxPieces + piecesPerBurst)
						: prevPieces;

				const burstCenterX = 15 + Math.random() * 70; // Keep bursts away from extreme edges
				const burstCenterY = 20 + Math.random() * 60;

				const burstPieces = Array.from({ length: piecesPerBurst }, () => {
					const angle = Math.random() * Math.PI * 2;
					const distance = 140 + Math.random() * 200;
					const travelX = Math.cos(angle) * distance;
					const travelY = Math.sin(angle) * distance;
					const startRotation = Math.random() * 360;
					const endRotation =
						startRotation +
						(Math.random() > 0.5 ? 360 : -360) * (0.5 + Math.random());

					return {
						id: `${confettiKey}-${Date.now()}-${Math.random()}`,
						color: colors[Math.floor(Math.random() * colors.length)],
						shape: shapes[Math.floor(Math.random() * shapes.length)],
						left: burstCenterX,
						top: burstCenterY,
						delay: Math.random() * 0.25,
						duration: 1.4 + Math.random() * 0.6,
						size: 6 + Math.random() * 10,
						startRotation,
						endRotation,
						travelX,
						travelY,
					};
				});

				return [...limitedPieces, ...burstPieces];
			});
		}, generationInterval);

		// Stop generating after the specified duration
		const stopGenerationTimer = setTimeout(() => {
			clearInterval(generationTimer);
		}, generationDuration);

		// Clear all confetti pieces after the longest possible animation duration
		// This ensures all pieces have time to complete their fade animation
		const maxAnimationDuration = 8000; // 8 seconds total (6s generation + 2s buffer)
		const clearTimer = setTimeout(() => {
			setConfettiPieces([]);
		}, maxAnimationDuration);

		return () => {
			clearInterval(generationTimer);
			clearTimeout(stopGenerationTimer);
			clearTimeout(clearTimer);
		};
	}, [confettiActive, confettiKey]);

	if (confettiPieces.length === 0) {
		return null;
	}

	return (
		<>
			<style>
				{`
          @keyframes confettiBurst {
            0% {
              transform: translate3d(0, 0, 0) rotate(var(--start-rotation)) scale(0.4);
              opacity: 0;
            }
            15% {
              opacity: 1;
            }
            100% {
              transform: translate3d(var(--travel-x), var(--travel-y), 0) rotate(var(--end-rotation)) scale(1);
              opacity: 0;
            }
          }
        `}
			</style>
			<div className="pointer-events-none fixed inset-0 z-[1000] overflow-hidden">
				{confettiPieces.map((piece) => (
					<div
						key={piece.id}
						className="absolute"
						style={{
							left: `calc(${piece.left}% - ${piece.size / 2}px)`,
							top: `calc(${piece.top}% - ${piece.size / 2}px)`,
							width: `${piece.size}px`,
							height: `${piece.size}px`,
							backgroundColor: piece.color,
							borderRadius:
								piece.shape === "circle"
									? "50%"
									: piece.shape === "triangle"
										? "0"
										: "2px",
							animation: `confettiBurst ${piece.duration}s cubic-bezier(0.19, 1, 0.22, 1) ${piece.delay}s forwards`,
							clipPath:
								piece.shape === "triangle"
									? "polygon(50% 0%, 0% 100%, 100% 100%)"
									: "none",
							"--start-rotation": `${piece.startRotation}deg`,
							"--end-rotation": `${piece.endRotation}deg`,
							"--travel-x": `${piece.travelX}px`,
							"--travel-y": `${piece.travelY}px`,
							willChange: "transform, opacity",
							backfaceVisibility: "hidden",
							transformStyle: "preserve-3d",
						}}
					/>
				))}
			</div>
		</>
	);
};

export default ConfettiRain;
